// src/Pages/Users/UserForm.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Box,
  Paper,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
} from "@mui/material";
import { addUser, updateUserById, updateUserProfileImage } from "../../Services/api";
import { toast, ToastContainer } from "react-toastify";
import localization from "../../Utils/localization";
import {jwtDecode} from "jwt-decode";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import Cropper from "react-easy-crop";

/** ---------- HELPERS ---------- **/
function dataURLtoFile(dataUrl, filename) {
  if (!dataUrl) return Promise.resolve(new File([], filename));
  if (dataUrl.startsWith("blob:") || dataUrl.startsWith("http")) {
    return fetch(dataUrl)
      .then((r) => r.blob())
      .then((blob) => new File([blob], filename, { type: blob.type || "image/jpeg" }));
  }

  const arr = dataUrl.split(",");
  if (arr.length > 1 && arr[0].includes("base64")) {
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return Promise.resolve(new File([u8arr], filename, { type: mime }));
  }

  return Promise.resolve(new File([], filename));
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, outputName = "cropped.jpg") {
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute("crossOrigin", "anonymous");
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = url;
    });

  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      const file = new File([blob], outputName, { type: "image/jpeg" });
      resolve({ file, blob, url: URL.createObjectURL(blob) });
    }, "image/jpeg", 0.9);
  });
}

/** ---------- COMPONENT ---------- **/
const UserForm = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // profile preview only

  const [catalogueImages, setCatalogueImages] = useState([]); // { src, isLocal }
  const replaceInputRefs = useRef([]);

  const [formData, setFormData] = useState({
    profileFor: "",
    gender: "",
    currentLivingCountry: "",
    maritalStatus: "",
    heightFt: "",
    heightInch: "",
    motherTongue: "",
    name: "",
    fatherName: "",
    motherName: "",
    dob: "",
    religion: "",
    caste: "",
    subCaste: "",
    state: "",
    city: "",
    highestEducation: "",
    alternative_mobile: "",
    manglik_status: "",
    jobStatus: "",
    occupation: "",
    annualIncome: "",
    email: "",
    mobile: "",
    password: "",
    repeatPassword: "",
    profileImage: null,
  });

  const [errors, setErrors] = useState({
    password: "",
    repeatPassword: "",
    email: "",
    mobile: "",
  });

  // cropper state
  const [openCropDialog, setOpenCropDialog] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropIndex, setCropIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCroppingBusy, setIsCroppingBusy] = useState(false);

  // ---------- fields ----------
  const fieldsName = [
    { title: "Create Profile For", type: "menu", key: "profileFor", data: localization.profileFor, required: true },
    { title: "Gender", type: "menu", key: "gender", data: localization.genderType, required: true },
    { title: "Current Living Country", type: "menu", key: "currentLivingCountry", data: localization.living_Country, required: true },
    { title: "Marital Status", type: "menu", key: "maritalStatus", data: localization.maritalStatus, required: true },
    { title: "Height (ft)", type: "menu", key: "heightFt", data: localization.heightFt, required: true },
    { title: "Height (inch)", type: "menu", key: "heightInch", data: localization.heightIn, required: true },
    { title: "Mother Tongue", type: "menu", key: "motherTongue", data: localization.mother_tounge, required: true },
    { title: "Name", type: "text", key: "name", required: true },
    { title: "Father Name", type: "text", key: "fatherName", required: true },
    { title: "Mother Name", type: "text", key: "motherName", required: true },
    { title: "Date of birth", type: "date", key: "dob", required: true },
    { title: "Religion", type: "menu", key: "religion", data: localization.religion_type, required: true },
    { title: "Caste", type: "menu", key: "caste", data: localization.cast_type, required: true },
    { title: "State", type: "menu", key: "state", data: localization.stateList, required: true },
    { title: "City", type: "menu", key: "city", data: () => getCitiesByState(formData.state), required: true },
    { title: "Highest Education", type: "text", key: "highestEducation", required: true },
    { title: "Job Status", type: "menu", key: "jobStatus", data: localization.jobType, required: true },
    { title: "Occupation", type: "text", key: "occupation", required: true },
    { title: "Annual Income", type: "menu", key: "annualIncome", data: ["0 - 3", "3 - 7", "7 - 10", "10+"], required: true },
    { title: "Email", type: "email", key: "email", required: true },
    { title: "Mobile", type: "tel", key: "mobile", required: true },
    { title: "Alternative Mobile", type: "tel", key: "alternative_mobile", required: false },
    { title: "Manglik Status (मांगलिक दोष)", type: "menu", key: "manglik_status", data: ["Yes", "No"], required: true },
  ];

  function getCitiesByState(state) {
    if (state === "Bihar") {
      return [
        "Araria","Arwal","Aurangabad","Banka","Begusarai","Bhagalpur","Bhojpur","Buxar","Darbhanga","East Champaran","Gaya","Gopalganj","Jamui","Jehanabad","Kaimur","Katihar","Khagaria","Kishanganj","Lakhisarai","Madhepura","Madhubani","Munger","Muzaffarpur","Nalanda","Nawada","Patna","Purnia","Rohtas","Saharsa","Samastipur","Saran","Sheikhpura","Sheohar","Sitamarhi","Siwan","Supaul","Vaishali","West Champaran"
      ];
    } else if (state === "Uttar Pradesh") {
      return [
        "Agra","Aligarh","Ambedkar Nagar","Amethi","Amroha","Auraiya","Ayodhya","Azamgarh","Baghpat","Bahraich","Ballia","Balrampur","Banda","Barabanki","Bareilly","Basti","Bhadohi","Bijnor","Budaun","Bulandshahr","Chandauli","Chitrakoot","Deoria","Etah","Etawah","Farrukhabad","Fatehpur","Firozabad","Gautam Buddha Nagar","Ghaziabad","Ghazipur","Gonda","Gorakhpur","Hamirpur","Hapur","Hardoi","Hathras","Jalaun","Jaunpur","Jhansi","Kannauj","Kanpur Dehat","Kanpur Nagar","Kasganj","Kaushambi","Kheri","Kushinagar","Lalitpur","Lucknow","Maharajganj","Mahoba","Mainpuri","Mathura","Mau","Meerut","Mirzapur","Moradabad","Muzaffarnagar","Pilibhit","Pratapgarh","Prayagraj","Raebareli","Rampur","Saharanpur","Sambhal","Sant Kabir Nagar","Shahjahanpur","Shamli","Shravasti","Siddharthnagar","Sitapur","Sonbhadra","Sultanpur","Unnao","Varanasi"
      ];
    }
    return [];
  }

  // validators
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateMobile = (mobile) => /^[0-9]{10}$/.test(mobile);

  const validateForm = () => {
    let valid = true;
    const newErrors = { password: "", repeatPassword: "", email: "", mobile: "" };

    const requiredFields = fieldsName.filter((f) => f.required).map((f) => f.key);
    if (formData.caste === "Kayasth") requiredFields.push("subCaste");
    if (!location.state?.user) requiredFields.push("password", "repeatPassword");

    for (const key of requiredFields) {
      if (!formData[key]) {
        const fld = (fieldsName.find((f) => f.key === key) || {}).title || key;
        toast.error(`Please fill in ${fld}`, { position: "top-center" });
        valid = false;
        break;
      }
    }

    if (!location.state?.user) {
      if ((formData.password || "").length < 6) {
        newErrors.password = "Password must be at least 6 characters";
        valid = false;
      }
      if (formData.password !== formData.repeatPassword) {
        newErrors.repeatPassword = "Passwords do not match";
        valid = false;
      }
    }

    if (!validateEmail(formData.email)) { newErrors.email = "Please enter a valid email address"; valid = false; }
    if (!validateMobile(formData.mobile)) { newErrors.mobile = "Please enter a valid 10-digit mobile number"; valid = false; }

    setErrors(newErrors);
    return valid;
  };

  // helper: token getter
  const getAuthToken = () => {
    return localStorage.getItem("accessToken") || localStorage.getItem("token") || "";
  };

  // compare candidate url to existing catalogue set
  const isSameAsCatalogue = (candidateUrl, catalogueArray) => {
    if (!candidateUrl) return false;
    return (catalogueArray || []).some((c) => {
      const cs = c?.src || c;
      return cs === candidateUrl || (cs && candidateUrl && cs.endsWith(candidateUrl)) || (candidateUrl && cs && candidateUrl.endsWith(cs));
    });
  };

  // ---------- PROFILE upload ----------
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.warning("Image size should be less than 2MB", { position: "top-center" }); return; }
    if (!file.type.match("image.*")) { toast.warning("Please select an image file", { position: "top-center" }); return; }

    setFormData((prev) => ({ ...prev, profileImage: file }));
    setPreviewUrl(URL.createObjectURL(file)); // local preview immediately

    if (location.state?.user) {
      const formDataObj = new FormData();
      formDataObj.append("type", "userProfile");
      formDataObj.append("storage", "server");
      formDataObj.append("image", file);

      try {
        setLoading(true);
        const res = await updateUserProfileImage(location.state.user._id, formDataObj);
        console.log("profileRes", res);
        const base = process.env.REACT_APP_API_BASE_URL || "";
        const serverProfile = res?.data?.profileImage ? `${base}${res.data.profileImage}` : null;
        if (serverProfile && !isSameAsCatalogue(serverProfile, catalogueImages)) {
          // only set previewUrl from server if it's not same as catalogue
          setPreviewUrl(serverProfile);
        } else {
          // keep local preview (already set)
        }
        toast.success(res.message || "Profile image uploaded", { position: "top-center" });
      } catch (err) {
        console.error("Profile upload error:", err);
        toast.error("Failed to upload profile image", { position: "top-center" });
      } finally {
        setLoading(false);
      }
    }
  };

  // ---------- robust fetch uploader for catalogue (with logs) ----------
  const uploadCatalogueToServer = async (formDataObj) => {
    const base = process.env.REACT_APP_API_BASE_URL || "";
    const url = base ? `${base}/api/uploadCatalogueImages` : `/api/uploadCatalogueImages`;
    const token = getAuthToken();

    console.log("[uploadCatalogueToServer] POST", url, { hasToken: !!token });

    const options = {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formDataObj,
    };

    let lastErr = null;
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const res = await fetch(url, options);
        const text = await res.text();
        let json;
        try { json = text ? JSON.parse(text) : {}; } catch (e) { json = { raw: text }; }
        console.log(`[uploadCatalogueToServer] attempt ${attempt} status=${res.status}`, json);
        if (!res.ok) {
          const err = { status: res.status, body: json };
          throw err;
        }
        return json;
      } catch (err) {
        lastErr = err;
        console.error(`[uploadCatalogueToServer] attempt ${attempt} failed:`, err);
        if (attempt === 1) await new Promise((r) => setTimeout(r, 300));
      }
    }
    throw lastErr || new Error("Unknown upload error");
  };

  // ---------- Catalogue upload (multiple) ----------
  const handleCatalogueChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) { toast.warning("Each catalogue image must be less than 2MB", { position: "top-center" }); return; }
      if (!file.type.match("image.*")) { toast.warning("Please select only image files for catalogue", { position: "top-center" }); return; }
    }

    // local previews immediately
    const previews = files.map((f) => ({ src: URL.createObjectURL(f), isLocal: true }));
    setCatalogueImages((prev) => [...prev, ...previews]);

    if (location.state?.user) {
      const fd = new FormData();
      fd.append("type", "catalogue");
      fd.append("storage", "server");
      files.forEach((file) => fd.append("image", file));

      try {
        setLoading(true);
        const resJson = await uploadCatalogueToServer(fd);
        console.log("uploadRes (catalogue)", resJson);

        const base = process.env.REACT_APP_API_BASE_URL || "";
        // handle possible response shapes
        if (resJson?.data?.catalogueImages) {
          setCatalogueImages(resJson.data.catalogueImages.map((p) => ({ src: `${base}${p}`, isLocal: false })));
        } else if (resJson?.data?.imageUrls) {
          const arr = Array.isArray(resJson.data.imageUrls) ? resJson.data.imageUrls : [resJson.data.imageUrls];
          setCatalogueImages(arr.map((p) => ({ src: `${base}${p}`, isLocal: false })));
        } else {
          // keep local previews
        }
        toast.success(resJson.message || "Catalogue uploaded", { position: "top-center" });
      } catch (err) {
        console.error("Catalogue upload error:", err);
        toast.error("Failed to upload catalogue images", { position: "top-center" });
      } finally {
        setLoading(false);
        e.target.value = null;
      }
    } else {
      e.target.value = null;
    }
  };

  // ---------- Replace single catalogue (used by edit and crop) ----------
  const handleReplaceCatalogue = async (index, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.warning("Image must be less than 2MB", { position: "top-center" }); return; }
    if (!file.type.match("image.*")) { toast.warning("Please select an image file", { position: "top-center" }); return; }

    try {
      setLoading(true);

      // remove old server-side image (best-effort)
      const imgObj = catalogueImages[index];
      if (imgObj && !imgObj.isLocal && location.state?.user) {
        const base = process.env.REACT_APP_API_BASE_URL || "";
        const relativePath = imgObj.src.startsWith(base) ? imgObj.src.replace(base, "") : imgObj.src;
        try {
          await updateUserById(location.state.user._id, { remove_catalogue_image: relativePath });
        } catch (remErr) {
          console.warn("failed to remove old catalogue before replace", remErr);
        }
      }

      const fd = new FormData();
      fd.append("type", "catalogue");
      fd.append("storage", "server");
      fd.append("image", file);

      if (location.state?.user) {
        try {
          const uploadJson = await uploadCatalogueToServer(fd);
          console.log("replaceRes (catalogue) server response:", uploadJson);

          const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
          if (uploadJson?.data?.catalogueImages) {
            setCatalogueImages(uploadJson.data.catalogueImages.map((p) => ({ src: `${baseUrl}${p}`, isLocal: false })));
          } else if (uploadJson?.data?.imageUrls) {
            const arr = Array.isArray(uploadJson.data.imageUrls) ? uploadJson.data.imageUrls : [uploadJson.data.imageUrls];
            setCatalogueImages(arr.map((p) => ({ src: `${baseUrl}${p}`, isLocal: false })));
          } else {
            // fallback: update local preview at index
            const newSrc = URL.createObjectURL(file);
            setCatalogueImages((prev) => {
              const copy = [...prev];
              copy[index] = { src: newSrc, isLocal: true };
              return copy;
            });
          }
        } catch (serverErr) {
          console.error("catalogue upload error (direct)", serverErr);
          let msg = "Failed to replace catalogue image on server";
          if (serverErr && serverErr.body) {
            if (typeof serverErr.body === "string") msg += `: ${serverErr.body}`;
            else if (serverErr.body.message) msg += `: ${serverErr.body.message}`;
            else msg += `: ${JSON.stringify(serverErr.body)}`;
          } else if (serverErr && serverErr.status) {
            msg += ` (status ${serverErr.status})`;
          }
          toast.error(`${msg}; showing local preview`, { position: "top-center" });

          const newSrc = URL.createObjectURL(file);
          setCatalogueImages((prev) => {
            const copy = [...prev];
            copy[index] = { src: newSrc, isLocal: true };
            return copy;
          });
          return;
        }
      } else {
        // not saved yet: update local preview
        const newSrc = URL.createObjectURL(file);
        setCatalogueImages((prev) => {
          const copy = [...prev];
          copy[index] = { src: newSrc, isLocal: true };
          return copy;
        });
      }

      toast.success("Catalogue image replaced", { position: "top-center" });
    } catch (err) {
      console.error("Replace catalogue error:", err);
      toast.error("Failed to replace catalogue image", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  // delete catalogue
  const handleDeleteCatalogue = async (index) => {
    const imageToDelete = catalogueImages[index];
    const base = process.env.REACT_APP_API_BASE_URL || "";
    let relativePath = null;
    if (imageToDelete && !imageToDelete.isLocal) {
      relativePath = imageToDelete.src.startsWith(base) ? imageToDelete.src.replace(base, "") : imageToDelete.src;
    }

    try {
      setLoading(true);
      if (relativePath && location.state?.user) {
        const res = await updateUserById(location.state.user._id, { remove_catalogue_image: relativePath });
        console.log("deleteRes (catalogue)", res);
        if (res?.data?.catalogueImages) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
          setCatalogueImages(res.data.catalogueImages.map((p) => ({ src: `${baseUrl}${p}`, isLocal: false })));
        } else {
          setCatalogueImages((prev) => prev.filter((_, i) => i !== index));
        }
        toast.success(res.message || "Catalogue image removed", { position: "top-center" });
      } else {
        setCatalogueImages((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (err) {
      console.error("Delete catalogue error:", err);
      toast.error("Failed to delete catalogue image", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  // crop dialog open
  const openCropForIndex = (index) => {
    const img = catalogueImages[index];
    if (!img) return;
    setCropIndex(index);
    setCropImageSrc(img.src);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setOpenCropDialog(true);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    if (cropIndex === null || !cropImageSrc || !croppedAreaPixels) {
      toast.warning("Please select and crop an area first", { position: "top-center" });
      return;
    }
    try {
      setIsCroppingBusy(true);
      const { file } = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      await handleReplaceCatalogue(cropIndex, file);
      setOpenCropDialog(false);
      setCropImageSrc(null);
      setCropIndex(null);
    } catch (err) {
      console.error("Crop/save error:", err);
      toast.error("Failed to crop/save image", { position: "top-center" });
    } finally {
      setIsCroppingBusy(false);
    }
  };

  const handleViewFullClose = () => {
    setOpenCropDialog(false);
    setCropImageSrc(null);
    setCropIndex(null);
  };

  // load user data into form
  useEffect(() => {
    if (location.state?.user) {
      const user = location.state.user;
      const match = user?.height?.match(/(\d+)'(\d+)"/);
      const feet = match ? parseInt(match[1], 10) : 0;
      const inches = match ? parseInt(match[2], 10) : 0;

      const cleanedUser = {
        ...user,
        profileFor: user.profile_for,
        gender: user.gender,
        currentLivingCountry: user.living_country,
        maritalStatus: user.marital_status,
        motherTongue: user.mother_tongue,
        fatherName: user.father_name,
        motherName: user.mother_name,
        jobStatus: user.job_status,
        caste: user.cast,
        subCaste: user.sub_caste,
        highestEducation: user.highest_education,
        annualIncome: user.annual_income,
        heightFt: feet ? `${feet}ft` : "",
        heightInch: inches ? `${inches}in` : "",
        city: user.city,
        dob: user.dob ? new Date(user.dob).toISOString().split("T")[0] : "",
      };

      setFormData(cleanedUser);

      const base = process.env.REACT_APP_API_BASE_URL || "";
      const catalogueUrls = Array.isArray(user.catalogueImages) ? user.catalogueImages.map((p) => `${base}${p}`) : [];

      if (user.profileImage) {
        const profileCandidate = `${base}${user.profileImage}`;
        // Defensive: if backend mistakenly returned same path for profile and catalogue, prefer catalogue list for catalogueImages
        if (!catalogueUrls.includes(profileCandidate)) {
          setPreviewUrl(profileCandidate);
        } else {
          console.warn("Ignored profileImage from server because it matches a catalogue image (defensive).");
          setPreviewUrl(null);
        }
      } else {
        setPreviewUrl(null);
      }

      if (user.catalogueImages && Array.isArray(user.catalogueImages)) {
        setCatalogueImages(user.catalogueImages.map((p) => ({ src: `${base}${p}`, isLocal: false })));
      } else {
        setCatalogueImages([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "state") setFormData((prev) => ({ ...prev, city: "" }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!previewUrl) { toast.warning(localization.selectImage, { position: "top-center" }); return; }

    setLoading(true);
    const heightStr = `${(formData.heightFt || "").replace("ft", "")}'${(formData.heightInch || "").replace("in", "")}"`;

    const params = {
      annual_income: formData.annualIncome,
      cast: formData.caste,
      city: formData.city,
      dob: new Date(formData.dob).toISOString().split("T")[0],
      father_name: formData.fatherName,
      gender: formData.gender,
      height: heightStr,
      highest_education: formData.highestEducation,
      job_status: formData.jobStatus,
      marital_status: formData.maritalStatus,
      mother_name: formData.motherName,
      mother_tongue: formData.motherTongue,
      name: formData.name,
      occupation: formData.occupation,
      profile_for: formData.profileFor,
      religion: formData.religion,
      state: formData.state,
      sub_caste: formData.caste === "Kayasth" ? formData.subCaste : "",
      living_country: formData.currentLivingCountry,
      email: formData.email,
      mobile: formData.mobile,
      alternative_mobile: formData.alternative_mobile,
      manglik_status: formData.manglik_status,
    };

    if (!location.state?.user) params.password = formData.password;

    try {
      const res = location.state?.user?._id
        ? await updateUserById(location.state.user._id, params)
        : await addUser(params);

      let userId = location.state?.user?._id;
      if (!userId && res?.data?.accessToken) {
        const decoded = jwtDecode(res.data?.accessToken);
        userId = decoded?.userId;
      }

      // if new user and profile image selected -> upload profile image
      if (!location.state?.user && formData.profileImage && userId) {
        const fd = new FormData();
        fd.append("type", "userProfile");
        fd.append("storage", "server");
        fd.append("image", formData.profileImage);
        try {
          const profileRes = await updateUserProfileImage(userId, fd);
          console.log("profileRes on create", profileRes);
          const base = process.env.REACT_APP_API_BASE_URL || "";
          const serverProfile = profileRes?.data?.profileImage ? `${base}${profileRes.data.profileImage}` : null;
          if (serverProfile && !isSameAsCatalogue(serverProfile, catalogueImages)) setPreviewUrl(serverProfile);
        } catch (err) {
          console.warn("Profile upload after signup failed:", err);
        }
      }

      // upload local catalogue images (isLocal true)
      const localCatalogueImages = catalogueImages.filter((c) => c.isLocal);
      if (localCatalogueImages.length && userId) {
        const fd = new FormData();
        fd.append("type", "catalogue");
        fd.append("storage", "server");
        for (let i = 0; i < localCatalogueImages.length; i++) {
          const img = localCatalogueImages[i];
          const filename = `catalogue_${Date.now()}_${i}.jpg`;
          // eslint-disable-next-line no-await-in-loop
          const file = await dataURLtoFile(img.src, filename);
          fd.append("image", file);
        }
        try {
          const uploadRes = await uploadCatalogueToServer(fd);
          console.log("uploadRes (catalogue post-save)", uploadRes);
          const base = process.env.REACT_APP_API_BASE_URL || "";
          if (uploadRes?.data?.catalogueImages) {
            setCatalogueImages(uploadRes.data.catalogueImages.map((p) => ({ src: `${base}${p}`, isLocal: false })));
          } else if (uploadRes?.data?.imageUrls) {
            const arr = Array.isArray(uploadRes.data.imageUrls) ? uploadRes.data.imageUrls : [uploadRes.data.imageUrls];
            setCatalogueImages(arr.map((p) => ({ src: `${base}${p}`, isLocal: false })));
          } else {
            setCatalogueImages((prev) => prev.map((p) => ({ ...p, isLocal: false })));
          }
        } catch (uploadErr) {
          console.error("Failed to upload local catalogue images after save:", uploadErr);
          toast.error("Saved profile but failed to upload some catalogue images.", { position: "top-center" });
        }
      }

      toast.success(location.state?.user ? "User updated successfully" : "User added successfully", { position: "top-center" });
      setTimeout(() => navigate("/users"), 1200);
    } catch (err) {
      console.error("Form submit error:", err);
      toast.error(err.response?.data?.message || "An error occurred. Please try again.", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  // ---------- RENDER helpers ----------
  const renderMenuField = (info) => (
    <Grid item xs={6} sm={3} key={info.key} sx={{ mb: 2 }}>
      <FormControl fullWidth required={info.required} sx={{ minWidth: 120 }}>
        <InputLabel id={`${info.key}-label`}>{info.title}</InputLabel>
        <Select
          labelId={`${info.key}-label`}
          name={info.key}
          value={typeof info.data === "function" ? (formData[info.key] || "") : (formData[info.key] || "")}
          label={info.title}
          onChange={handleChange}
          sx={{ "& .MuiSelect-select": { padding: "12.5px 14px" } }}
        >
          {(typeof info.data === "function" ? info.data() : (info.data || [])).map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );

  const renderTextField = (info) => (
    <Grid item xs={6} sm={3} key={info.key} sx={{ mb: 2 }}>
      <TextField
        fullWidth
        name={info.key}
        label={info.title}
        value={formData[info.key] || ""}
        onChange={handleChange}
        required={info.required}
        type={info.type}
        InputLabelProps={info.type === "date" ? { shrink: true } : {}}
        error={!!errors[info.key]}
        helperText={errors[info.key]}
        variant="outlined"
        sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderRadius: "4px" } } }}
      />
    </Grid>
  );

  // JSX
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <ToastContainer />
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
          {location.state?.user ? "Edit User Profile" : "Create New Profile"}
        </Typography>

        {/* PROFILE + CATALOGUE */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start", maxWidth: 520, width: "100%", px: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 auto" }}>
              <Box sx={{ position: "relative" }}>
                <Avatar src={previewUrl} sx={{ width: 120, height: 120, border: "2px solid #f5f5f5" }} />
                {previewUrl && (
                  <IconButton
                    color="error"
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      backgroundColor: "rgba(255,255,255,0.9)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                      p: 0.3,
                      width: 28,
                      height: 28,
                      minWidth: 28,
                    }}
                    onClick={() => {
                      setPreviewUrl(null);
                      setFormData((prev) => ({ ...prev, profileImage: null }));
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
                {catalogueImages.length ? (
                  catalogueImages.map((img, idx) => {
                    if (!replaceInputRefs.current[idx]) replaceInputRefs.current[idx] = React.createRef();
                    return (
                      <Box key={idx} sx={{ position: "relative", width: 60, height: 60, cursor: "pointer" }}>
                        <img
                          src={img.src}
                          alt={`catalogue-${idx}`}
                          style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6 }}
                          onClick={() => openCropForIndex(idx)}
                        />

                        {/* <Box sx={{ position: "absolute", top: -6, right: -6, display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            sx={{ backgroundColor: "rgba(255,255,255,0.95)", p: 0.25, width: 28, height: 28, minWidth: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
                            onClick={() => replaceInputRefs.current[idx].current.click()}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            sx={{ backgroundColor: "rgba(255,255,255,0.95)", p: 0.25, width: 28, height: 28, minWidth: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", color: "#e53935" }}
                            onClick={() => handleDeleteCatalogue(idx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box> */}

                        <input
                          ref={replaceInputRefs.current[idx]}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(ev) => {
                            const file = ev.target.files?.[0];
                            if (file) handleReplaceCatalogue(idx, file);
                            ev.target.value = null;
                          }}
                        />
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">No catalogue images</Typography>
                )}
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "center" }}>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                  Upload Profile Image
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </Button>

                {/* <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                  Upload Catalogue Image(s)
                  <input type="file" accept="image/*" hidden multiple onChange={handleCatalogueChange} />
                </Button> */}
              </Box>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {fieldsName.map((info) => (info.type === "menu" ? renderMenuField(info) : renderTextField(info)))}

            {/* conditional subCaste */}
            {formData.caste === "Kayasth" && (
              <Grid item xs={6} sm={3} sx={{ mb: 2 }}>
                <FormControl fullWidth required sx={{ minWidth: 120 }}>
                  <InputLabel id={`subCaste-label`}>Sub Caste</InputLabel>
                  <Select labelId={`subCaste-label`} name="subCaste" value={formData.subCaste || ""} label="Sub Caste" onChange={handleChange}>
                    {(localization.subCast_type || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* password fields when creating */}
            {!location.state?.user && (
              <>
                <Grid item xs={6} sm={3} sx={{ mb: 2 }}>
                  <TextField fullWidth name="password" label="Password" value={formData.password || ""} onChange={handleChange} required type="password" error={!!errors.password} helperText={errors.password} />
                </Grid>
                <Grid item xs={6} sm={3} sx={{ mb: 2 }}>
                  <TextField fullWidth name="repeatPassword" label="Repeat Password" value={formData.repeatPassword || ""} onChange={handleChange} required type="password" error={!!errors.repeatPassword} helperText={errors.repeatPassword} />
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
              <Button variant="outlined" color="secondary" onClick={() => navigate("/users")} disabled={loading} sx={{ px: 4, py: 1 }}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" type="submit" disabled={loading} startIcon={loading && <CircularProgress size={20} color="inherit" />} sx={{ px: 4, py: 1 }}>
                {location.state?.user ? "Update" : "Create"} Profile
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog open={openCropDialog} onClose={handleViewFullClose} fullWidth maxWidth="md">
        <DialogTitle>View & Crop Image</DialogTitle>
        <DialogContent dividers sx={{ height: 520, display: "flex", flexDirection: "column", gap: 2 }}>
          {cropImageSrc ? (
            <>
              <Box sx={{ position: "relative", flex: 1, background: "#111" }}>
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2">Zoom</Typography>
                <Slider value={zoom} min={1} max={3} step={0.1} onChange={(e, v) => setZoom(v)} sx={{ flex: 1 }} />
              </Box>
            </>
          ) : (
            <Typography>No image selected</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleViewFullClose} disabled={isCroppingBusy}>Cancel</Button>
          <Button onClick={handleCropSave} variant="contained" disabled={isCroppingBusy}>
            {isCroppingBusy ? <CircularProgress size={18} color="inherit" /> : "Save Cropped"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserForm;
